
// Vue reactivity
import { computed, defineComponent, ref } from 'vue';

// icons
import { close, arrowBack, pencil, trashOutline, eyeOutline, add, } from 'ionicons/icons';

// components
import { IonPage, IonHeader, IonFooter, IonToolbar, IonTitle, IonContent,
        IonButtons, IonButton, IonIcon, IonCard, IonCardHeader, IonCardContent,
        IonGrid, IonList, IonItem, IonLabel, IonAccordionGroup, IonAccordion, IonSpinner,
        modalController, loadingController, alertController } from '@ionic/vue';
import ABProfessionSelectModal from '@/components/pss/profession/ABProfessionSelectModal.vue';

// types
import { Profession, ProfessionTab, Step1Option } from '@/types';

// composables
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from '@/store';
import { utils } from '@/composables/utils';

// service
import ABSService from '@/services/ABSService';

export default defineComponent({
  name: 'AB4CustomizeContentModal',
  props: ["isPage"],
  components: { IonPage, IonHeader, IonFooter, IonToolbar, IonTitle, IonContent,
                IonButtons, IonButton, IonIcon, IonCard, IonCardHeader, IonCardContent,
                IonGrid, IonList, IonItem, IonLabel, IonAccordionGroup, IonAccordion, IonSpinner, },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();

    const loadingData = computed(() => store.state.loadingPortalData);
    const user = computed(() => store.state.user);
    const step1Questions = computed(() => store.getters.getStep1Questions(false, false));
    const allProfessions = computed(() => store.state.allProfessions);
    const allProfessionTabs = computed<ProfessionTab[]>(() => store.state.professionTabs);

    const { openImageModal, presentPrompt, presentToast, presentAlert, uniqueId, openModal, } = utils();

    const closeModal = async () => {
      if (props.isPage) {
        router.replace('/home');
      } else {
        await modalController.dismiss();
      }
    };

    const promptNewOptText = async (message, prefilledText = "", callback) => {
      const alert = await alertController.create({
        message,
        inputs: [
          {
            name: 'newText',
            type: 'text',
            placeholder: "New text",
            value: prefilledText,
            attributes: { required: true, }
          },
        ],
        buttons: [
          {
            text: t('back'),
            role: 'cancel',
            cssClass: 'secondary',
          }, {
            text: t('submit'),
            handler: (value) => {
              if (value.newText) {
                callback(value.newText);
              } else {
                presentAlert("New text cannot be empty!")
                return false;
              }
            }
          }
        ]
      });
      return alert.present();
    }

    const insertChangeRecord = async (toastMsg, type, optionId, questionId, prevText, newText = null) => {
      const loading = await loadingController.create({});
      await loading.present();
      ABSService.insertStep1ChangeRecord(type, optionId, questionId, prevText, newText);
      presentToast(toastMsg);
      loading.dismiss();
    }

    // 3. return variables & methods to be used in template HTML
    return {
      // icons
      close, arrowBack, pencil, trashOutline, eyeOutline, add,
      
      // variables
      loadingData, step1Questions,
      user,
      
      // methods
      closeModal, openImageModal,

      relatedProfessions: (opt) => {
        const { tabIds, professionIds } = opt;
        let tabProfessionIds = [];
        for (const tid of (tabIds || [])) {
          const pTab = allProfessionTabs.value.find(pt => pt.id == tid);
          if (pTab) tabProfessionIds = tabProfessionIds.concat(pTab.relatedProfessionIds);
        }
        const relatedProfessionIds = professionIds?.length > 0 ? professionIds : tabProfessionIds; // override
        const filteredProfessions = [
          ...(relatedProfessionIds || []).map(id => (allProfessions.value.find((p: Profession) => p.id == id.toString()))),
        ].filter(p => p != null);
        return filteredProfessions;
      },

      // Step 1 Change Records
      deleteTag: (options, idx) => {
        const opt: Step1Option = options[idx];
        presentPrompt(`Are you sure to delete '${opt.text}'?`, () => {
          options.splice(idx, 1);
          insertChangeRecord("Item deleted successfully.", "Delete", opt.id, opt.questionId, opt.text);
        })
      },
      updateTag: async (opt) => {
        await promptNewOptText(`Updating '${opt.text}'`, opt.text, async (newText) => {
          opt.text = newText;
          insertChangeRecord("Item updated successfully!", "Edit", opt.id, opt.questionId, opt.text, newText);
        })
      },
      addNewTag: async (question) => {
        await promptNewOptText(`Adding new item`, "", async (newText) => {
          const { id: questionId } = question;
          const newOptId = `to-${uniqueId()}`;
          question.options.unshift({ id: newOptId, questionId, text: newText });
          insertChangeRecord("Item added successfully!", "Add Tag", newOptId, questionId, "", newText);
        })
      },

      // Preview
      openProfessionSelectModal: async () =>  {
        return await openModal(ABProfessionSelectModal, { isPreview: true }, 'tall-modal');
      },
    }
  }
});
